import { useState, type FormEvent } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6g3d7qhq9pk5mectm0";

const values = [
  { icon: "ri-compass-3-line", title: "聚合多元", text: "把影视、音乐与资讯汇聚一处，减少在多平台之间来回切换的成本。" },
  { icon: "ri-flashlight-line", title: "快速获取", text: "用资讯快报与热门榜单，让你在最短时间内掌握最新娱乐动态。" },
  { icon: "ri-heart-3-line", title: "用心筛选", text: "每一份推荐都经过编辑挑选，兼顾话题度与内容质量。" },
];

const contacts = [
  { icon: "ri-mail-line", label: "联系邮箱", value: "hello@kuaimao.example" },
  { icon: "ri-phone-line", label: "合作电话", value: "400-000-0000" },
  { icon: "ri-map-pin-line", label: "办公地址", value: "北京市朝阳区内容创意园 A 座 8 层" },
  { icon: "ri-time-line", label: "服务时间", value: "周一至周五 09:30 - 18:30" },
];

export default function About() {
  useSeo({
    title: "关于我们 - 快猫",
    description: "了解快猫的品牌定位、服务宗旨与联系方式。快猫致力于打造多元娱乐内容与最新资讯聚合平台，为娱乐爱好者提供优质内容体验。",
    keywords: "关于快猫,快猫品牌,娱乐平台介绍,联系方式,快猫团队",
  });

  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormSuccess("感谢你的反馈，我们会尽快处理。");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => body.append(key, String(value)));

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormSuccess(serverMsg || "提交成功，感谢你的反馈！");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试。");
    }
  };

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-8 md:py-8">
        <Breadcrumb items={[{ label: "关于我们" }]} />

        <section className="mt-4 overflow-hidden rounded-lg bg-foreground-950">
          <div className="relative px-6 py-14 md:px-12 md:py-20">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20warm%20orange%20gradient%20waves%20with%20soft%20geometric%20shapes%20and%20gentle%20light%20particles%2C%20modern%20minimal%20brand%20background%2C%20clean%20elegant%20composition%2C%20no%20text&width=1600&height=700&seq=about-hero-01&orientation=landscape"
              alt="快猫品牌视觉"
              title="快猫 - 多元娱乐内容聚合平台"
              className="absolute inset-0 h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/30" />
            <div className="relative z-10 max-w-2xl">
              <span className="flex w-fit items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 font-label text-xs font-semibold text-background-50">
                <i className="ri-play-circle-line" />
                关于快猫
              </span>
              <h1 className="mt-4 font-heading text-3xl font-extrabold text-background-50 md:text-4xl">
                多元娱乐内容与最新资讯聚合平台
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-background-200 md:text-base">
                快猫面向广大娱乐爱好者，聚合影视资讯、音乐推荐与动态娱乐新闻。我们希望用清晰的分类、贴心的筛选与实时的资讯快报，让每一次内容浏览都轻松而愉快。
              </p>
            </div>
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-heading text-2xl font-extrabold text-foreground-950">品牌介绍</h2>
          <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-[1.4fr_1fr]">
            <div className="space-y-4 text-[15px] leading-8 text-foreground-800">
              <p>
                快猫诞生于对娱乐内容的热爱。我们发现，追剧的人、听歌的人、关注热点的人，往往需要在不同平台之间来回切换，才能拼凑出完整的娱乐体验。于是我们决定做一个“聚合平台”：把影视、音乐与资讯放在同一个入口，让兴趣的流动不再有门槛。
              </p>
              <p>
                在内容上，我们坚持编辑筛选与口碑并重；在体验上，我们提供多维筛选、关键词搜索与相关推荐，帮助每一位用户高效地找到自己真正想看、想听的内容。未来，快猫会持续打磨内容结构与浏览体验，陪伴每一位娱乐爱好者的日常。
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-1">
              {values.map((v) => (
                <div key={v.title} className="rounded-lg border border-background-200/80 bg-background-50 p-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className={`${v.icon} text-xl`} />
                  </span>
                  <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">{v.title}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-foreground-600">{v.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="font-heading text-2xl font-extrabold text-foreground-950">联系方式</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {contacts.map((c) => (
              <div key={c.label} className="rounded-lg border border-background-200/80 bg-background-50 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                  <i className={`${c.icon} text-xl`} />
                </span>
                <p className="mt-3 font-label text-xs font-medium text-foreground-500">{c.label}</p>
                <p className="mt-1 text-sm font-semibold text-foreground-900">{c.value}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <div className="grid grid-cols-1 gap-8 rounded-lg border border-background-200/80 bg-background-50 p-6 md:p-8 lg:grid-cols-[1fr_1.2fr]">
            <div>
              <h2 className="font-heading text-2xl font-extrabold text-foreground-950">联系与反馈</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                对快猫有任何建议、合作意向或内容反馈，欢迎留言告诉我们。我们会认真阅读每一条消息，并在工作时间内尽快回复。
              </p>
              <ul className="mt-5 space-y-3 text-sm text-foreground-700">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-primary-500" />
                  内容纠错与建议
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-primary-500" />
                  商务合作与投稿
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-primary-500" />
                  产品体验反馈
                </li>
              </ul>
            </div>

            <form
              id="kuaimao-feedback-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-4"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="kf-name" className="mb-1.5 block font-label text-sm font-medium text-foreground-700">
                    你的称呼
                  </label>
                  <input
                    id="kf-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入姓名或昵称"
                    className="w-full rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="kf-email" className="mb-1.5 block font-label text-sm font-medium text-foreground-700">
                    联系邮箱
                  </label>
                  <input
                    id="kf-email"
                    name="email"
                    type="email"
                    required
                    placeholder="name@example.com"
                    className="w-full rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="kf-subject" className="mb-1.5 block font-label text-sm font-medium text-foreground-700">
                  反馈类型
                </label>
                <select
                  id="kf-subject"
                  name="subject"
                  defaultValue="内容建议"
                  className="w-full rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400"
                >
                  <option value="内容建议">内容建议</option>
                  <option value="商务合作">商务合作</option>
                  <option value="内容纠错">内容纠错</option>
                  <option value="产品反馈">产品反馈</option>
                </select>
              </div>

              <div>
                <label htmlFor="kf-message" className="mb-1.5 block font-label text-sm font-medium text-foreground-700">
                  留言内容
                </label>
                <textarea
                  id="kf-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请填写你的建议或问题（最多 500 字）"
                  className="w-full resize-none rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400"
                />
              </div>

              <input
                className="alt-contact-verify"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 sm:w-fit"
              >
                <i className="ri-send-plane-line" />
                提交反馈
              </button>

              {status === "success" && (
                <p className="flex items-center gap-2 rounded-md bg-primary-100/70 px-3 py-2.5 text-sm text-primary-700">
                  <i className="ri-checkbox-circle-fill" />
                  {formSuccess}
                </p>
              )}
              {status === "error" && (
                <p className="flex items-center gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-700">
                  <i className="ri-error-warning-fill" />
                  {formError}
                </p>
              )}
            </form>
          </div>
        </section>

        <p className="mt-10 text-center text-xs text-foreground-500">
          © {new Date().getFullYear()} 快猫 KUAIMAO · 本站内容均为演示样例，仅用于产品展示
        </p>
      </div>
    </SiteLayout>
  );
}